import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { cn } from "@/components/podkit/lib/cn";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { type FC, type ReactNode, useState } from "react";

export type CollapsibleSectionProps = {
    title: string;
    headers: string[];
    children: ReactNode;
    isLoading?: boolean;
    isEmpty?: boolean;
};

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({ title, headers, children, isLoading, isEmpty }) => {
    const [isOpen, setIsOpen] = useState(true);

    if (isLoading) {
        return null;
    }

    if (isEmpty) {
        return null;
    }

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="bg-surface-secondary">
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        className="hover:bg-muted/5 flex w-full items-center justify-between p-4 py-2 text-left"
                        data-tracking-id="toggle-resource-section"
                    >
                        <span className="font-medium">{title}</span>
                        <IconChevronRight
                            size="sm"
                            className={cn("shrink-0 transition-transform", isOpen && "rotate-90")}
                        />
                    </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                    <Table>
                        <TableHeader className="border-b border-border-base [&_th:first-child]:rounded-none [&_th:last-child]:rounded-none [&_th]:bg-transparent">
                            <TableRow>
                                {headers.map((header) => (
                                    <TableHead key={header}>{header}</TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody className="border-b border-border-base">{children}</TableBody>
                    </Table>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};
